import type { ReadyWhenClause } from '@de/providers/DataSourceManager.types';

export type { ReadyWhenClause };

/**
 * Present means not `null` and not `undefined`.
 * `''`, `0`, and `false` are present.
 */
export function isPresentValue(value: unknown): boolean {
  return value !== null && value !== undefined;
}

function readPath(root: unknown, path: string): unknown {
  if (path.length === 0) return root;
  let current = root;
  for (const segment of path.split('.')) {
    if (
      current === null ||
      current === undefined ||
      typeof current !== 'object'
    ) {
      return undefined;
    }
    if (!Object.prototype.hasOwnProperty.call(current, segment)) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function isClauseList(
  readyWhen: ReadyWhenClause | readonly ReadyWhenClause[],
): readyWhen is readonly ReadyWhenClause[] {
  return Array.isArray(readyWhen);
}

function clausesOf(
  readyWhen: ReadyWhenClause | readonly ReadyWhenClause[],
): readonly ReadyWhenClause[] {
  if (isClauseList(readyWhen)) {
    return readyWhen;
  }
  return [readyWhen];
}

/**
 * Source names a declaration waits on, whether or not `dependsOn` repeats them.
 *
 * Scheduling uses this list so a gate can settle without the author listing
 * the same source twice. Names are de-duplicated; `dependsOn` order is kept.
 */
export function executionDependencyNames(
  decl:
    | {
        readonly dependsOn?: readonly string[];
        readonly readyWhen?: ReadyWhenClause | readonly ReadyWhenClause[];
      }
    | undefined,
): readonly string[] {
  if (decl === undefined) return [];
  const names = [...(decl.dependsOn ?? [])];
  const seen = new Set(names);
  if (decl.readyWhen !== undefined) {
    for (const clause of clausesOf(decl.readyWhen)) {
      if (seen.has(clause.source)) continue;
      seen.add(clause.source);
      names.push(clause.source);
    }
  }
  return names;
}

/**
 * True when every `readyWhen` path is present on the interpolation context.
 * No clause means the gate is open.
 */
export function readyWhenSatisfied(
  readyWhen: ReadyWhenClause | readonly ReadyWhenClause[] | undefined,
  context: Readonly<Record<string, unknown>>,
): boolean {
  if (readyWhen === undefined) return true;
  return clausesOf(readyWhen).every((clause) =>
    isPresentValue(readPath(context[clause.source], clause.path)),
  );
}
